<!--
  @component ScanModal
  @description Modal for scanning ISBN barcodes and searching catalogs
-->
<script lang="ts">
  import { createEventDispatcher, onDestroy, tick } from 'svelte';
  import { t } from '@/i18n';
  import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
  import { searchISBN, type ISBNSearchResult } from '@services/isbnSearchService';
  import type { Creator } from '@types';

  export let isOpen = false;

  const dispatch = createEventDispatcher<{
    close: void;
    found: { result: ISBNSearchResult };
  }>();

  // Scanner state
  const scannerContainerId = 'isbn-scanner-container';
  let html5QrCode: Html5Qrcode | null = null;
  let scanning = false;
  let scannedISBN = '';
  let error = '';
  let isInitializing = false;
  let scannerContainer: HTMLDivElement;

  // Search state
  let isSearching = false;
  let searchResult: ISBNSearchResult | null = null;
  let searchedSources: string[] = [];
  let notFound = false;

  // Source labels for display
  const sourceLabels: Record<string, string> = {
    sudoc: 'Sudoc',
    bnf: 'BnF',
    swisscovery: 'Swisscovery',
    harvardLibrary: 'Harvard Library',
    openLibrary: 'Open Library',
  };

  onDestroy(() => {
    stopScanner();
  });

  // Watch for isOpen changes
  $: if (isOpen) {
    initScanner();
  } else {
    resetState();
  }

  function resetState() {
    stopScanner();
    scannedISBN = '';
    error = '';
    isSearching = false;
    searchResult = null;
    searchedSources = [];
    notFound = false;
  }

  async function initScanner() {
    // Wait for DOM to be updated
    await tick();
    // Additional small delay to ensure html5-qrcode can find the element
    setTimeout(() => {
      if (isOpen && !scanning && !scannedISBN && !error && !isInitializing && !isSearching) {
        startScanner();
      }
    }, 150);
  }

  async function startScanner() {
    if (isInitializing || scanning) return;

    isInitializing = true;
    error = '';

    try {
      const container = document.getElementById(scannerContainerId);
      if (!container) {
        console.error('Scanner container not found in DOM');
        throw new Error('Scanner container not found');
      }

      console.log('Starting scanner...');

      const formatsToSupport = [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
      ];

      html5QrCode = new Html5Qrcode(scannerContainerId, {
        formatsToSupport,
        verbose: false,
      });

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 15,
          qrbox: { width: 280, height: 120 },
          aspectRatio: 1.777,
        },
        async (decodedText) => {
          console.log('Barcode detected:', decodedText);
          if (decodedText.startsWith('978') || decodedText.startsWith('979')) {
            scannedISBN = decodedText;
            await stopScanner();
            await searchForISBN(decodedText);
          }
        },
        () => {}
      );

      scanning = true;
      console.log('Scanner started successfully');
    } catch (err) {
      console.error('Scanner error:', err);
      if (err instanceof Error) {
        if (err.message.includes('NotAllowedError') || err.message.includes('Permission')) {
          error = $t('scan.cameraAccessDenied') || 'Camera access denied.';
        } else if (err.message.includes('NotFoundError') || err.message.includes('no camera')) {
          error = $t('scan.noCameraFound') || 'No camera found.';
        } else {
          error = `${$t('errors.generic') || 'Error'}: ${err.message}`;
        }
      } else {
        error = $t('errors.generic') || 'Failed to start camera.';
      }
    } finally {
      isInitializing = false;
    }
  }

  async function stopScanner() {
    scanning = false;
    isInitializing = false;

    if (html5QrCode) {
      try {
        const state = html5QrCode.getState();
        if (state === 2) {
          await html5QrCode.stop();
          console.log('Scanner stopped');
        }
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      html5QrCode = null;
    }
  }

  async function searchForISBN(isbn: string) {
    isSearching = true;
    notFound = false;
    searchResult = null;

    try {
      const response = await searchISBN(isbn);
      searchedSources = response.searchedSources;

      if (response.success && response.result) {
        searchResult = response.result;
        console.log('Found result:', searchResult);
      } else {
        notFound = true;
        console.log('ISBN not found');
      }
    } catch (err) {
      console.error('Search error:', err);
      error = $t('errors.generic') || 'Search failed';
    } finally {
      isSearching = false;
    }
  }

  function handleClose() {
    resetState();
    dispatch('close');
  }

  async function handleScanAnother() {
    scannedISBN = '';
    searchResult = null;
    notFound = false;
    searchedSources = [];
    await tick();
    setTimeout(() => startScanner(), 200);
  }

  function handleImport() {
    if (searchResult) {
      dispatch('found', { result: searchResult });
      handleClose();
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOpen) {
      handleClose();
    }
  }

  function formatCreators(creators: Creator[] | readonly Creator[] | undefined): string {
    if (!creators || creators.length === 0) return '';
    return (creators as Creator[])
      .slice(0, 3)
      .map((c) => c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim())
      .filter(Boolean)
      .join(', ');
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div class="modal-backdrop" on:click={handleBackdropClick}>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="scan-modal-title">
      <div class="modal-header">
        <h2 id="scan-modal-title">{$t('scan.title') || 'Scan ISBN'}</h2>
        <button class="close-button" on:click={handleClose} aria-label={$t('common.close')}>
          &times;
        </button>
      </div>

      <div class="modal-content">
        {#if error}
          <!-- Error State -->
          <div class="error-state">
            <span class="error-icon">⚠️</span>
            <p class="error-message">{error}</p>
            <button class="btn btn-primary" on:click={handleScanAnother}>
              {$t('common.retry') || 'Retry'}
            </button>
          </div>
        {:else if isSearching}
          <!-- Searching State -->
          <div class="searching-state">
            <span class="spinner large"></span>
            <p class="isbn-scanned">ISBN: <strong>{scannedISBN}</strong></p>
            <p class="searching-text">{$t('scan.searchingCatalogs') || 'Searching catalogs...'}</p>
            <div class="sources-list">
              {#each searchedSources as source}
                <span class="source-badge">{sourceLabels[source] || source}</span>
              {/each}
            </div>
          </div>
        {:else if searchResult}
          <!-- Result Found State -->
          <div class="result-state">
            <div class="result-header">
              <span class="success-badge">✓</span>
              <span class="source-found">{$t('scan.foundIn') || 'Found in'} {sourceLabels[searchResult.source]}</span>
            </div>

            <div class="result-card">
              <h3 class="result-title">{searchResult.item.title || 'Sans titre'}</h3>

              {#if searchResult.item.creators && searchResult.item.creators.length > 0}
                <p class="result-field">
                  <span class="field-label">{$t('item.fields.creators') || 'Authors'}:</span>
                  <span class="field-value">{formatCreators(searchResult.item.creators)}</span>
                </p>
              {/if}

              {#if searchResult.item.date}
                <p class="result-field">
                  <span class="field-label">{$t('item.fields.date') || 'Date'}:</span>
                  <span class="field-value">{searchResult.item.date}</span>
                </p>
              {/if}

              {#if searchResult.item.publisher}
                <p class="result-field">
                  <span class="field-label">{$t('item.fields.publisher') || 'Publisher'}:</span>
                  <span class="field-value">{searchResult.item.publisher}</span>
                </p>
              {/if}

              {#if searchResult.item.place}
                <p class="result-field">
                  <span class="field-label">{$t('item.fields.place') || 'Place'}:</span>
                  <span class="field-value">{searchResult.item.place}</span>
                </p>
              {/if}

              {#if searchResult.item.ISBN}
                <p class="result-field">
                  <span class="field-label">ISBN:</span>
                  <span class="field-value isbn">{searchResult.item.ISBN}</span>
                </p>
              {/if}

              {#if searchResult.item.numPages}
                <p class="result-field">
                  <span class="field-label">{$t('item.fields.numPages') || 'Pages'}:</span>
                  <span class="field-value">{searchResult.item.numPages}</span>
                </p>
              {/if}
            </div>

            <div class="result-actions">
              <button class="btn btn-secondary" on:click={handleScanAnother}>
                {$t('scan.scanAnother') || 'Scan another'}
              </button>
              <button class="btn btn-primary" on:click={handleImport}>
                {$t('common.import') || 'Import'}
              </button>
            </div>
          </div>
        {:else if notFound}
          <!-- Not Found State -->
          <div class="not-found-state">
            <span class="not-found-icon">🔍</span>
            <p class="isbn-scanned">ISBN: <strong>{scannedISBN}</strong></p>
            <p class="not-found-text">{$t('scan.notFound') || 'No record found in catalogs'}</p>
            <div class="sources-searched">
              <p class="sources-label">{$t('scan.searchedIn') || 'Searched in'}:</p>
              <div class="sources-list">
                {#each searchedSources as source}
                  <span class="source-badge">{sourceLabels[source] || source}</span>
                {/each}
              </div>
            </div>
            <button class="btn btn-primary" on:click={handleScanAnother}>
              {$t('scan.scanAnother') || 'Scan another'}
            </button>
          </div>
        {:else}
          <!-- Scanner State -->
          <div class="camera-container">
            <div bind:this={scannerContainer} id={scannerContainerId} class="scanner-element"></div>
            {#if isInitializing}
              <div class="initializing-overlay">
                <span class="spinner"></span>
                <span>{$t('common.loading') || 'Loading...'}</span>
              </div>
            {/if}
          </div>
          <p class="scan-hint">{$t('scan.hint') || 'Point your camera at an ISBN barcode'}</p>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .modal {
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: 10000;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .modal-header h2 {
    margin: 0;
    font-size: 18px;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    color: var(--color-text-secondary, #aaa);
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-content {
    padding: 20px;
    overflow-y: auto;
  }

  /* Camera/Scanner */
  .camera-container {
    position: relative;
    width: 100%;
    min-height: 300px;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
  }

  .scanner-element {
    width: 100%;
    min-height: 300px;
  }

  .scanner-element :global(video) {
    border-radius: 8px;
    width: 100% !important;
  }

  .scanner-element :global(#qr-shaded-region) {
    border-color: var(--color-accent, #5c7cfa) !important;
  }

  .initializing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    gap: 12px;
    z-index: 10;
  }

  .scan-hint {
    text-align: center;
    color: var(--color-text-secondary, #aaa);
    font-size: 13px;
    margin-top: 12px;
    margin-bottom: 0;
  }

  /* Spinner */
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .spinner.large {
    width: 48px;
    height: 48px;
    border-width: 3px;
    border-color: rgba(92, 124, 250, 0.3);
    border-top-color: var(--color-accent, #5c7cfa);
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Error State */
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
  }

  .error-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .error-message {
    color: var(--color-text-secondary, #aaa);
    margin-bottom: 20px;
  }

  /* Searching State */
  .searching-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
  }

  .isbn-scanned {
    margin-top: 16px;
    font-size: 14px;
    color: var(--color-text-secondary, #aaa);
  }

  .isbn-scanned strong {
    font-family: monospace;
    color: var(--color-text-primary, #e8e8e8);
  }

  .searching-text {
    margin-top: 8px;
    color: var(--color-text-secondary, #aaa);
  }

  .sources-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    margin-top: 12px;
  }

  .source-badge {
    background: var(--color-bg-tertiary, #4a4a4a);
    color: var(--color-text-secondary, #aaa);
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
  }

  /* Result State */
  .result-state {
    padding: 10px 0;
  }

  .result-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }

  .success-badge {
    width: 28px;
    height: 28px;
    background: var(--color-success, #40c057);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
  }

  .source-found {
    color: var(--color-text-secondary, #aaa);
    font-size: 14px;
  }

  .result-card {
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }

  .result-title {
    margin: 0 0 12px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary, #e8e8e8);
    line-height: 1.4;
  }

  .result-field {
    margin: 8px 0;
    font-size: 13px;
    display: flex;
    gap: 8px;
  }

  .field-label {
    color: var(--color-text-secondary, #aaa);
    min-width: 80px;
    flex-shrink: 0;
  }

  .field-value {
    color: var(--color-text-primary, #e8e8e8);
  }

  .field-value.isbn {
    font-family: monospace;
  }

  .result-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  /* Not Found State */
  .not-found-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
  }

  .not-found-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .not-found-text {
    color: var(--color-text-secondary, #aaa);
    margin-bottom: 16px;
  }

  .sources-searched {
    margin-bottom: 20px;
  }

  .sources-label {
    color: var(--color-text-muted, #888);
    font-size: 12px;
    margin-bottom: 8px;
  }

  /* Buttons */
  .btn {
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    border: 1px solid var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover {
    background: var(--color-accent-hover, #4c6ef5);
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover, #555);
  }
</style>
